import { api } from './api';
import type { ProspectType } from './prospects.api';

// Miroir de l'enum Prisma `JeopardyStatus`. En V1 seul `at_risk` est produit ;
// `resolved` est la couture socle (évolution future « Résolu »), non exposée
// en écriture côté UI.
export type JeopardyStatus = 'at_risk' | 'resolved';

export interface JeopardyEntry {
  id: string;
  contractNumber: string;
  // Décimaux sérialisés en chaîne (Prisma Decimal → string). Convertir via
  // Number() côté affichage.
  annualPremiumHt: string;
  annualCommission: string;
  // DATE sérialisée en ISO datetime (minuit UTC). `slice(0, 10)` pour l'afficher
  // / la réinjecter dans un <input type="date">.
  dueDate: string;
  reason: string;
  status: JeopardyStatus;
  createdAt: string;
  updatedAt: string;
  prospect: {
    id: string;
    type: ProspectType;
    companyName: string | null;
    firstName: string | null;
    lastName: string | null;
    status: string;
    assignedUserId: string;
    assignedTeamId: string;
  };
  contractType: { id: string; code: string; label: string };
  creator: { id: string; firstName: string; lastName: string };
}

export interface JeopardyListResponse {
  data: JeopardyEntry[];
  hasMore: boolean;
  cursor: string | null;
}

export interface JeopardyFilters {
  cursor?: string;
  limit?: number;
  prospect_id?: string;
  contract_type_id?: string;
  status?: JeopardyStatus;
  contract_number?: string;
  search?: string;
  assigned_user_id?: string;
  team_id?: string;
  min_premium?: number;
  max_premium?: number;
  min_commission?: number;
  max_commission?: number;
  due_after?: string;
  due_before?: string;
  sort?: 'due_date_asc' | 'due_date_desc' | 'created_desc';
}

function auth(token: string) {
  return { accessToken: token };
}

function qs(filters: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const jeopardyApi = {
  list: (token: string, filters: JeopardyFilters = {}) =>
    api.get<JeopardyListResponse>(
      `/api/v1/jeopardy${qs(filters as Record<string, unknown>)}`,
      auth(token),
    ),

  get: (token: string, id: string) => api.get<JeopardyEntry>(`/api/v1/jeopardy/${id}`, auth(token)),

  create: (token: string, body: Record<string, unknown>) =>
    api.post<JeopardyEntry>('/api/v1/jeopardy', body, auth(token)),

  update: (token: string, id: string, body: Record<string, unknown>) =>
    api.patch<JeopardyEntry>(`/api/v1/jeopardy/${id}`, body, auth(token)),

  remove: (token: string, id: string) => api.delete<void>(`/api/v1/jeopardy/${id}`, auth(token)),

  /** Vérification de doublon non bloquante (client + n° de contrat). */
  duplicateCheck: (token: string, prospectId: string, contractNumber: string, excludeId?: string) =>
    api.get<{ duplicate: boolean; count: number }>(
      `/api/v1/jeopardy/duplicate-check${qs({
        prospect_id: prospectId,
        contract_number: contractNumber,
        exclude_id: excludeId,
      })}`,
      auth(token),
    ),
};
